'use client';

import { type RefObject, useEffect, useState } from 'react';

interface MediaPlaybackState {
  isPlaying: boolean;
  isMuted: boolean;
}

export function useMediaPlaybackState(videoRef: RefObject<HTMLVideoElement | null>): MediaPlaybackState {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const readState = () => {
      setIsPlaying(!video.paused && !video.ended);
      setIsMuted(video.muted);
    };

    readState();

    video.addEventListener('play', readState);
    video.addEventListener('pause', readState);
    video.addEventListener('ended', readState);
    video.addEventListener('volumechange', readState);
    video.addEventListener('loadedmetadata', readState);
    video.addEventListener('emptied', readState);

    return () => {
      video.removeEventListener('play', readState);
      video.removeEventListener('pause', readState);
      video.removeEventListener('ended', readState);
      video.removeEventListener('volumechange', readState);
      video.removeEventListener('loadedmetadata', readState);
      video.removeEventListener('emptied', readState);
    };
  }, [videoRef]);

  return { isPlaying, isMuted };
}
